import { FunctionComponent, useMemo } from 'react';
import { FaPoundSign } from 'react-icons/fa';
import { FaDollarSign, FaEuroSign } from 'react-icons/fa6';
import { FaChartLine } from 'react-icons/fa6';
import { AUMTime } from '../types/dashboard.types';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Tooltip as MuiTooltip } from '@mui/material';
import { getSparklineDomain } from '../utils/chart.utils';

interface BaseCurrencyAUMProps {
  baseCurrency: string;
  aum: number;
  aumList?: AUMTime[];
  showChart?: boolean;
  title?: string;
}

const BaseCurrencyAUM: FunctionComponent<BaseCurrencyAUMProps> = ({
  baseCurrency,
  aum,
  aumList,
  showChart = false,
  title = 'Current AUM',
}) => {
  const CurrencyIcon = useMemo(() => {
    switch (baseCurrency) {
      case 'USD':
        return <FaDollarSign className="text-green-500" />;
      case 'EUR':
        return <FaEuroSign className="text-green-500" />;
      case 'GBP':
        return <FaPoundSign className="text-green-500" />;
      default:
        return <FaEuroSign className="text-green-500" />;
    }
  }, [baseCurrency]);

  const chartData = useMemo(() => {
    return (aumList || []).map((d) => ({
      date: d.date,
      aum: Math.round(d.aum),
    }));
  }, [aumList]);

  const shouldShowChart = showChart && chartData.length >= 2;
  const yDomain = useMemo(
    () => getSparklineDomain(chartData.map((d) => d.aum)),
    [chartData],
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        {CurrencyIcon}
        {title}
      </div>
      <div className="mt-1.5 text-lg font-bold tracking-tight text-gray-900">
        {baseCurrency} {Math.floor(aum).toLocaleString()}
      </div>

      {showChart && (
        <div className="mt-1 h-16">
          {shouldShowChart ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                className="rounded border border-gray-200 bg-white"
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              >
                <defs>
                  <linearGradient id="aumGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#16a34a" stopOpacity={0.6} />{' '}
                    <stop offset="100%" stopColor="#ffffff" stopOpacity={1} />
                  </linearGradient>
                </defs>

                <XAxis dataKey="date" hide />
                <YAxis hide domain={yDomain} />

                <Tooltip
                  formatter={(value: number) =>
                    `${baseCurrency} ${value.toLocaleString()}`
                  }
                  labelFormatter={(label) => `Date: ${label}`}
                  position={{ y: 50 }}
                />

                <Area
                  type="monotone"
                  dataKey="aum"
                  name="AUM"
                  stroke="#16a34a"
                  strokeWidth={2}
                  fill="url(#aumGradient)"
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <MuiTooltip title="Chart available for data over 1 day" arrow>
              <div className="bg-gray-25 flex h-full items-center justify-center rounded border border-gray-100">
                <FaChartLine className="text-xs text-gray-400" />
              </div>
            </MuiTooltip>
          )}
        </div>
      )}
    </div>
  );
};

export default BaseCurrencyAUM;
